/**
 * FN-SUB — Fizetés szükséges modal
 * grace/locked állapotban blokkolja az interfészt
 * - Tárolt kártyánál: azonnali Barion one-click terhelés
 * - Nincs kártya: Barion setup-payment-method gateway-re irányít
 * - Jogosultság: Adminisztrátor vagy Vezető
 */
import { useState, useEffect } from 'react';
import { useFeatureContext } from '../../contexts/FeatureContext';
import { get, post } from '../../lib/api';

interface SubInfo {
  total_monthly_filler: number;
  total_monthly_gross_filler: number;
  subscription: { plan_code: string; billing_cycle: string };
}

const PLAN_NAMES: Record<string, string> = { start: 'Start', standard: 'Standard', pro: 'Pro' };
const CYCLE_LABELS: Record<string, string> = { monthly: 'havi', yearly: 'éves' };

function fmtHuf(filler: number) {
  return Math.round(filler / 100).toLocaleString('hu-HU') + ' Ft';
}

export default function PaymentRequiredModal() {
  const { status, dataPurgeAt, refresh } = useFeatureContext();
  const [subInfo, setSubInfo] = useState<SubInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<null | 'success' | 'not_admin' | 'no_card' | 'failed'>(null);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (status === 'grace' || status === 'locked') {
      get<SubInfo>('/subscriptions/current').then(setSubInfo).catch(() => {});
    }
  }, [status]);

  if (status !== 'grace' && status !== 'locked') return null;

  const isLocked = status === 'locked';
  const purgeDate = dataPurgeAt ? new Date(dataPurgeAt) : null;
  const purgeDateStr = purgeDate
    ? purgeDate.toLocaleDateString('hu-HU', { year: 'numeric', month: 'long', day: 'numeric' })
    : null;
  const amount = subInfo ? fmtHuf(subInfo.total_monthly_gross_filler) : '…';
  const planLabel = subInfo
    ? `Modulum ${PLAN_NAMES[subInfo.subscription.plan_code] ?? subInfo.subscription.plan_code} — ${CYCLE_LABELS[subInfo.subscription.billing_cycle] ?? subInfo.subscription.billing_cycle}`
    : '';

  const handlePay = async () => {
    setLoading(true);
    setResult(null);
    try {
      const res = await post<{ success: boolean; action?: string; gatewayUrl?: string; error?: string }>(
        '/subscriptions/pay-now', {},
      );
      if (res.success) {
        setResult('success');
        setTimeout(() => { refresh(); window.location.reload(); }, 2000);
      } else if ((res.action === 'redirect_to_barion' || res.action === 'setup_payment_method') && res.gatewayUrl) {
        window.location.href = res.gatewayUrl;
      } else {
        setResult('failed');
        setErrorMsg(res.error || 'A fizetési oldal megnyitása sikertelen. Kérjük próbálja újra.');
      }
    } catch (err: unknown) {
      const e = err as { status?: number };
      if (e?.status === 403) {
        setResult('not_admin');
      } else {
        setResult('failed');
        setErrorMsg('Hálózati hiba. Kérjük próbálja újra.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOpenBilling = () => {
    window.location.href = '/beallitasok/szamlazas';
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(15,23,42,0.85)',
      backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '16px',
    }}>
      <div style={{
        background: '#fff', borderRadius: 16, maxWidth: 480, width: '100%',
        boxShadow: '0 25px 60px rgba(0,0,0,0.3)', overflow: 'hidden',
      }}>
        {/* Fejléc */}
        <div style={{
          background: isLocked ? '#7f1d1d' : '#dc2626',
          padding: '24px 28px', display: 'flex', alignItems: 'center', gap: 14,
        }}>
          <span style={{ fontSize: 32 }}>{isLocked ? '🚨' : '🔒'}</span>
          <div>
            <div style={{ color: '#fff', fontWeight: 800, fontSize: 18, lineHeight: 1.2 }}>
              {isLocked ? 'Adatai törlésre kerülnek' : 'Fizetés szükséges'}
            </div>
            <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 13, marginTop: 4 }}>
              {isLocked ? 'Az előfizetési díj rendezése sürgős' : 'Előfizetési díj esedékes'}
            </div>
          </div>
        </div>

        {/* Tartalom */}
        <div style={{ padding: '24px 28px' }}>
          {result === 'success' ? (
            <div style={{ textAlign: 'center', padding: '16px 0' }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
              <div style={{ fontWeight: 700, fontSize: 18, color: '#16a34a', marginBottom: 8 }}>Sikeres fizetés!</div>
              <div style={{ color: '#6b7280', fontSize: 14 }}>Az előfizetése megújult. Az oldal hamarosan frissül…</div>
            </div>
          ) : (
            <>
              {/* Összeg blokk */}
              {subInfo && (
                <div style={{
                  background: '#f9fafb', border: '1px solid #e5e7eb',
                  borderRadius: 10, padding: '14px 18px', marginBottom: 18,
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                }}>
                  <div>
                    <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 2 }}>Fizetendő összeg</div>
                    <div style={{ fontSize: 13, color: '#374151' }}>{planLabel}</div>
                  </div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: isLocked ? '#7f1d1d' : '#dc2626' }}>
                    {amount}
                  </div>
                </div>
              )}

              {isLocked && purgeDateStr && (
                <div style={{
                  background: '#fef2f2', border: '1.5px solid #fca5a5',
                  borderRadius: 10, padding: '12px 16px', marginBottom: 18,
                  display: 'flex', gap: 10, alignItems: 'flex-start',
                }}>
                  <span style={{ fontSize: 18, flexShrink: 0 }}>⚠️</span>
                  <div>
                    <div style={{ fontWeight: 700, color: '#dc2626', fontSize: 13 }}>Adattörlési határidő: <strong>{purgeDateStr}</strong></div>
                    <div style={{ color: '#7f1d1d', fontSize: 12, marginTop: 3, lineHeight: 1.5 }}>
                      Ezt követően az összes CRM adat véglegesen törlésre kerül. A kiállított számlák megőrzésre kerülnek.
                    </div>
                  </div>
                </div>
              )}

              <p style={{ color: '#374151', fontSize: 13, lineHeight: 1.65, marginBottom: 18 }}>
                {isLocked
                  ? 'A CRM adataihoz való hozzáférés zárolva van. Az adatok törlésének elkerüléséhez rendezze azonnal a díjat.'
                  : 'Az előfizetési díj levonása sikertelen volt. Kérjük rendezze a díjat — a fizetés Barion bankkártyás fizetéssel történik.'}
              </p>

              {result === 'not_admin' && (
                <div style={{ background: '#fef9c3', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 13, color: '#92400e' }}>
                  <strong>Adminisztrátor vagy Vezető jogosultság szükséges.</strong><br />
                  Kérje meg szervezete erre jogosult munkatársát a díj rendezésére.
                </div>
              )}
              {result === 'failed' && (
                <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 13, color: '#dc2626' }}>
                  {errorMsg}
                </div>
              )}
              {result === 'no_card' && (
                <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 13, color: '#1e40af' }}>
                  Nincs tárolt bankkártya. A számlázási beállításoknál adja meg kártyaadatait.
                </div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <button onClick={handlePay} disabled={loading} style={btnStyle(isLocked ? '#7f1d1d' : '#dc2626')}>
                  {loading ? 'Fizetés folyamatban…' : '💳 Fizessen most — Barion'}
                </button>
                <button onClick={handleOpenBilling} style={{
                  background: 'transparent', border: '1px solid #e5e7eb',
                  borderRadius: 8, padding: '10px 16px', fontSize: 13,
                  cursor: 'pointer', color: '#6b7280',
                }}>
                  Számlázási beállítások →
                </button>
              </div>

              <p style={{ color: '#9ca3af', fontSize: 11, marginTop: 16, textAlign: 'center' }}>
                Kérdés esetén: <a href="mailto:info@modulum.hu" style={{ color: '#14233F' }}>info@modulum.hu</a>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function btnStyle(bg: string): React.CSSProperties {
  return {
    background: bg, color: '#fff', border: 'none',
    borderRadius: 8, padding: '12px 20px',
    fontSize: 15, fontWeight: 700, cursor: 'pointer',
    width: '100%', textAlign: 'center',
  };
}
